import type { Ohaeng } from '../types/index';
import type { FourPillars } from '../types/index';
import type { YongSinResult } from '../types/index';
import { getStemElement, getElementRelation, isOvercoming } from '../core/five-elements';
import { findHanSin, ALL_ELEMENTS } from './yong-sin';

/**
 * 통관법 (通關法) — 두 강한 오행이 상극할 때 중재 오행 투입
 *
 * 원리: 사주 내 두 오행이 모두 강하면서 서로 상극 관계일 때,
 * 생성 사이클 상 양쪽을 잇는 중재 오행을 용신으로 선정
 */

const STRONG_THRESHOLD = 2.0;

const TONGGWAN_MAP: Record<Ohaeng, Partial<Record<Ohaeng, Ohaeng>>> = {
  '木': { '土': '火' },  // 木克土 → 火 통관
  '土': { '水': '金' },  // 土克水 → 金 통관
  '水': { '火': '木' },  // 水克火 → 木 통관
  '火': { '金': '土' },  // 火克金 → 土 통관
  '金': { '木': '水' },  // 金克木 → 水 통관
};

interface ClashPair {
  attacker: Ohaeng;
  defender: Ohaeng;
  mediator: Ohaeng;
  intensity: number;
}

/**
 * 통관법에 의한 용신 판단
 */
export function determineTonggwanYongSin(
  fourPillars: FourPillars,
  ohaengDistribution: Record<Ohaeng, number>,
): YongSinResult {
  const dayMasterElement = getStemElement(fourPillars.day.stem);

  const clashPairs: ClashPair[] = [];

  for (const a of ALL_ELEMENTS) {
    for (const b of ALL_ELEMENTS) {
      if (a === b) continue;
      if (!isOvercoming(a, b)) continue;
      if (ohaengDistribution[a] < STRONG_THRESHOLD || ohaengDistribution[b] < STRONG_THRESHOLD) continue;

      const mediator = TONGGWAN_MAP[a][b]!;
      const intensity = Math.min(ohaengDistribution[a], ohaengDistribution[b]);
      clashPairs.push({ attacker: a, defender: b, mediator, intensity });
    }
  }

  clashPairs.sort((a, b) => b.intensity - a.intensity);

  let yongSinElement: Ohaeng;
  let applicability: number;
  let description: string;

  if (clashPairs.length > 0) {
    const strongest = clashPairs[0];
    yongSinElement = strongest.mediator;
    applicability = Math.min(1.0, 0.5 + (strongest.intensity - STRONG_THRESHOLD) * 0.25);
    description = `${strongest.attacker}(${ohaengDistribution[strongest.attacker].toFixed(1)})과 ${strongest.defender}(${ohaengDistribution[strongest.defender].toFixed(1)})의 상극 충돌, ${yongSinElement}이(가) 통관 용신 (${strongest.attacker}→${yongSinElement}→${strongest.defender})`;
  } else {
    const relation = getElementRelation(dayMasterElement);
    yongSinElement = relation.generatedBy;
    applicability = 0.1;
    description = '뚜렷한 상극 충돌이 없어 통관법 적합도 낮음';
  }

  const relation = getElementRelation(yongSinElement);
  const huiSin = relation.generates;
  const giSin = relation.overcomeBy;
  const guSin = getElementRelation(huiSin).overcomeBy;
  const hanSin = findHanSin(yongSinElement, huiSin, giSin, guSin);

  return {
    method: '통관',
    yongSin: yongSinElement,
    huiSin,
    giSin,
    guSin,
    hanSin,
    applicability,
    description,
  };
}
